"use client";

/**
 * Generic match prediction hook.
 *
 * Encapsulates prediction lifecycle (auth → submit → success),
 * score/selection state, betslip sync, odds fetching, and outcome
 * derivation for any supported prediction market.
 *
 * Used by PredictorMatchCard (CORRECT_SCORE) and future selection-based
 * prediction components (FT_1X2, BTTS, OVER_UNDER, etc.).
 */
import { useState, useCallback, useMemo, useEffect } from "react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import { useAuth } from "@/lib/auth/AuthContext";
import { markActivity } from "@/lib/auth/activityTracker";
import { useBetslip } from "@/features/betslip/useBetslip";
import { useServerBatchMatchOdds } from "@/features/odds/useServerBatchMatchOdds";
import { toMatchOddsMap } from "@/features/odds/adapters";
import type { BookmakerOdds, OddsData, OperatorData } from "@/features/odds/types";
import type { MarketType } from "fansunited-data-layer";
import { deriveOutcomeKey, scoreToValue, valueToScores } from "../utils/deriveOutcome";
import { getSdkMarketEnum, getDefaultOddsMarket, isScoreBasedMarket, type PredictionMarket } from "../utils/markets";
import type { ExistingPrediction } from "./useMyPredictions";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface PredictionResult {
  success: boolean;
  predictionId?: string;
  error?: string;
}
export interface TeamInfo {
  name: string;
  shortName?: string;
  assets?: {
    logo?: unknown;
  };
}
export interface UseMatchPredictionParams {
  matchId: string;
  homeTeam: TeamInfo;
  awayTeam: TeamInfo;
  kickoffAt: string;
  /** Prediction market – defaults to CORRECT_SCORE */
  market?: PredictionMarket;
  /** Override the odds market passed to useBatchMatchOdds */
  oddsMarket?: MarketType;
  /** Pre-populate the prediction value */
  existingPrediction?: ExistingPrediction;
  /** Called after a successful prediction */
  onSuccess?: (result: PredictionResult, value: string) => void;
}
export interface ScoreHelpers {
  home: string;
  away: string;
  increment: (team: "home" | "away") => void;
  decrement: (team: "home" | "away") => void;
}
export interface PredictedOutcome {
  outcomeKey: string;
  matchingOdd: OddsData | null;
  operator: OperatorData | null;
}
export interface UseMatchPredictionReturn {
  /** Current prediction value (e.g. "2:1" for CORRECT_SCORE, "1" for FT_1X2) */
  value: string;
  /** Set the prediction value (works for any market) */
  setValue: (val: string) => void;
  /** Score helpers — meaningful only for CORRECT_SCORE market */
  scores: ScoreHelpers;

  /** Whether a prediction has been submitted */
  isPredicted: boolean;
  /** Submit the current prediction */
  submit: () => Promise<PredictionResult>;
  /** Enter edit mode (reset predicted state) */
  edit: () => void;
  /** Whether a prediction is currently being submitted */
  isSubmitting: boolean;
  /** Result of the last submission attempt */
  lastResult: PredictionResult | null;
  /** Clear the last result */
  clearResult: () => void;

  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether to show the sign-in prompt */
  showSignInModal: boolean;
  setShowSignInModal: (v: boolean) => void;

  /** First bookmaker odds for the match */
  matchOdds: BookmakerOdds | null;
  /** Whether odds are still loading */
  oddsLoading: boolean;

  /** Derived outcome key + matching odd for the current prediction */
  predictedOutcome: PredictedOutcome | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_SCORE = 20;
const MIN_SCORE = 0;

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useMatchPrediction(params: UseMatchPredictionParams): UseMatchPredictionReturn {
  const {
    matchId,
    homeTeam,
    awayTeam,
    kickoffAt,
    market = "CORRECT_SCORE",
    oddsMarket,
    existingPrediction,
    onSuccess
  } = params;
  const scoreBased = isScoreBasedMarket(market);

  // --- External hooks ---
  const sdk = useFansUnitedSDK();
  const {
    user
  } = useAuth();
  const {
    addPrediction
  } = useBetslip();
  const isAuthenticated = !!user;

  // --- Internal state ---
  const [homeScore, setHomeScore] = useState<string>(existingPrediction && scoreBased ? String(existingPrediction.homeScore) : "");
  const [awayScore, setAwayScore] = useState<string>(existingPrediction && scoreBased ? String(existingPrediction.awayScore) : "");
  const [selectionValue, setSelectionValue] = useState<string>("");
  const [isPredicted, setIsPredicted] = useState(!!existingPrediction);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<PredictionResult | null>(null);
  const clearResult = useCallback(() => setLastResult(null), []);

  // --- Sync with existing prediction ---
  useEffect(() => {
    if (existingPrediction) {
      if (scoreBased) {
        setHomeScore(String(existingPrediction.homeScore));
        setAwayScore(String(existingPrediction.awayScore));
      }
      // TODO: for selection markets, parse existingPrediction value
      setIsPredicted(true);
    }
  }, [existingPrediction, scoreBased]);

  // --- Derived value ---
  const value = useMemo(() => {
    if (scoreBased) {
      // Only produce a value when at least one score has been set
      if (homeScore === "" && awayScore === "") return "";
      return scoreToValue(parseInt(homeScore || "0"), parseInt(awayScore || "0"));
    }
    return selectionValue;
  }, [scoreBased, homeScore, awayScore, selectionValue]);

  // --- setValue (generic) ---
  const setValue = useCallback((val: string) => {
    if (scoreBased) {
      const [h, a] = valueToScores(val);
      setHomeScore(String(h));
      setAwayScore(String(a));
    } else {
      setSelectionValue(val);
    }
  }, [scoreBased]);

  // --- Odds ---
  const resolvedOddsMarket = oddsMarket ?? getDefaultOddsMarket(market);
  const {
    oddsMap: rawOddsMap,
    loading: oddsLoading
  } = useServerBatchMatchOdds({
    matchIds: matchId ? [matchId] : [],
    markets: [resolvedOddsMarket]
  });
  const matchOdds = useMemo(() => {
    const converted = toMatchOddsMap(rawOddsMap);
    const list = converted.get(matchId);
    if (!list || list.length === 0) return null;
    return list[0];
  }, [rawOddsMap, matchId]);

  // --- Betslip sync (score-based only) ---
  const syncBetslip = useCallback((newHome: number, newAway: number) => {
    addPrediction(matchId, newHome, newAway, kickoffAt);
  }, [matchId, kickoffAt, addPrediction]);

  // --- Score helpers ---
  const incrementScore = useCallback((team: "home" | "away") => {
    if (team === "home") {
      const next = parseInt(homeScore || "0") + 1;
      if (next <= MAX_SCORE) {
        setHomeScore(next.toString());
        syncBetslip(next, parseInt(awayScore || "0"));
      }
    } else {
      const next_0 = parseInt(awayScore || "0") + 1;
      if (next_0 <= MAX_SCORE) {
        setAwayScore(next_0.toString());
        syncBetslip(parseInt(homeScore || "0"), next_0);
      }
    }
  }, [homeScore, awayScore, syncBetslip]);
  const decrementScore = useCallback((team_0: "home" | "away") => {
    if (team_0 === "home") {
      const next_1 = parseInt(homeScore || "0") - 1;
      if (next_1 >= MIN_SCORE) {
        setHomeScore(next_1.toString());
        syncBetslip(next_1, parseInt(awayScore || "0"));
      }
    } else {
      const next_2 = parseInt(awayScore || "0") - 1;
      if (next_2 >= MIN_SCORE) {
        setAwayScore(next_2.toString());
        syncBetslip(parseInt(homeScore || "0"), next_2);
      }
    }
  }, [homeScore, awayScore, syncBetslip]);
  const scores: ScoreHelpers = useMemo(() => ({
    home: homeScore,
    away: awayScore,
    increment: incrementScore,
    decrement: decrementScore
  }), [homeScore, awayScore, incrementScore, decrementScore]);

  // --- Predicted outcome (match value against odds) ---
  const predictedOutcome = useMemo((): PredictedOutcome | null => {
    const outcomeKey = deriveOutcomeKey(market, value);
    if (!outcomeKey) return null;
    let matchingOdd: OddsData | null = null;
    let operator: OperatorData | null = null;
    if (matchOdds) {
      matchingOdd = matchOdds.odds.find(odd => odd.label.toUpperCase() === outcomeKey || odd.label === outcomeKey) ?? null;
      operator = matchOdds.operator;
    }
    return {
      outcomeKey,
      matchingOdd,
      operator
    };
  }, [market, value, matchOdds]);

  // --- Submit ---
  const submit = useCallback(async (): Promise<PredictionResult> => {
    if (!isAuthenticated) {
      setShowSignInModal(true);
      return {
        success: false,
        error: "Not authenticated"
      };
    }
    if (!sdk) {
      const result: PredictionResult = {
        success: false,
        error: "SDK not available"
      };
      setLastResult(result);
      return result;
    }
    setIsSubmitting(true);
    setLastResult(null);
    try {
      // Normalise scores to "0" when empty
      let submissionValue: string;
      if (scoreBased) {
        const finalHome = homeScore || "0";
        const finalAway = awayScore || "0";
        setHomeScore(finalHome);
        setAwayScore(finalAway);
        submissionValue = scoreToValue(parseInt(finalHome), parseInt(finalAway));
      } else {
        submissionValue = selectionValue;
      }
      const sdkMarket = getSdkMarketEnum(market);
      const response = await sdk.predictor.makeFootballPrediction(matchId, sdkMarket, submissionValue);
      const result_1: PredictionResult = {
        success: true,
        predictionId: response?.id
      };
      if (user?.uid) markActivity(user.uid);
      setLastResult(result_1);
      setIsPredicted(true);
      onSuccess?.(result_1, submissionValue);
      return result_1;
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to submit prediction";
      const result_0: PredictionResult = {
        success: false,
        error: msg
      };
      setLastResult(result_0);
      return result_0;
    } finally {
      setIsSubmitting(false);
    }
  }, [isAuthenticated, sdk, scoreBased, homeScore, awayScore, selectionValue, market, matchId, onSuccess]);

  // --- Edit ---
  const edit = useCallback(() => {
    setIsPredicted(false);
    clearResult();
  }, [clearResult]);

  // --- Return ---
  return {
    value,
    setValue,
    scores,
    isPredicted,
    submit,
    edit,
    isSubmitting,
    lastResult,
    clearResult,
    isAuthenticated,
    showSignInModal,
    setShowSignInModal,
    matchOdds,
    oddsLoading,
    predictedOutcome
  };
}